import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { creditsAPI, subscriptionAPI } from '../../services/api';
import api from '../../services/api';
import { userAssistantConfigService } from '../../services/userAssistantConfigService';

export function isNewUser(createdAt: string): boolean {
  const createdTime = new Date(createdAt).getTime();
  const currentTime = new Date().getTime();
  return (currentTime - createdTime) < 5 * 60 * 1000;
}

export interface IntelligenceSummary {
  total_calls: number;
  total_leads_contacted: number;
  temperature_breakdown: Record<string, number>;
  disposition_breakdown: Record<string, number>;
  goal_achievement_rate: number;
  follow_ups_pending: number;
  recent_calls: Array<{
    id: string;
    lead_id: string;
    lead_name: string;
    lead_phone: string;
    run_id: string | null;
    timestamp: string | null;
    analysis_summary: string | null;
    lead_temperature: string | null;
    disposition: string | null;
    goal_outcome: string | null;
    engagement_score: number | null;
    follow_up_action: any;
    follow_up_status?: 'completed' | 'not_required' | null;
    follow_up_resolved_at?: string | null;
  }>;
  campaigns_with_pending_actions: string[];
  needs_attention: Array<{
    id: string;
    call_summary_id?: string;
    name: string;
    phone: string;
    current_temperature: string | null;
    current_disposition: string | null;
    last_contact_at: string | null;
    total_attempts: number;
    has_pending_follow_up: boolean;
    follow_up_description: string | null;
  }>;
}

export interface AnalyticsSummary {
  total_calls: number;
  successful_calls: number;
  failed_calls: number;
  average_duration: number;
  success_rate: number;
}

export function useDashboardPage() {
  const { user, loading: authLoading } = useAuth();
  const [error, setError] = useState('');
  const [subscriptionInfo, setSubscriptionInfo] = useState<{ plan: string; status: string } | null>(null);
  const [planNamesById, setPlanNamesById] = useState<Record<string, string>>({});
  const [isAccountConfigured, setIsAccountConfigured] = useState<boolean | null>(null);
  const [intelligence, setIntelligence] = useState<IntelligenceSummary | null>(null);
  const [intelligenceLoading, setIntelligenceLoading] = useState(true);
  const [analyticsSummary, setAnalyticsSummary] = useState<AnalyticsSummary | null>(null);

  const isUserNew = user?.created_at ? isNewUser(user.created_at) : false;

  const fetchAccountConfigStatus = async () => {
    try {
      const validation = await userAssistantConfigService.validateConfig();
      setIsAccountConfigured(validation.is_valid);
    } catch {
      setIsAccountConfigured(false);
    }
  };

  const fetchSubscriptionInfo = async () => {
    try {
      const creditStats = await creditsAPI.getBalance();
      setSubscriptionInfo({
        plan: creditStats.subscription_plan || 'free',
        status: creditStats.subscription_status || 'inactive'
      });
    } catch (err) {
      setSubscriptionInfo({ plan: 'free', status: user?.subscription_status || 'inactive' });
    }
  };

  const fetchPlanNames = async () => {
    try {
      const res = await subscriptionAPI.getSubscriptionLinks();
      const plans = res?.plans || {};
      const map: Record<string, string> = {};
      for (const [planId, plan] of Object.entries(plans as Record<string, { name?: string }>)) {
        if (plan?.name) map[String(planId).toLowerCase()] = plan.name;
      }
      setPlanNamesById(map);
    } catch {
      setPlanNamesById({});
    }
  };

  const fetchIntelligenceSummary = async () => {
    try {
      setIntelligenceLoading(true);
      const [intResponse, analyticsResponse] = await Promise.all([
        api.get('/analytics/intelligence-summary'),
        api.get('/analytics/dashboard?days=90').catch(() => null),
      ]);
      setIntelligence(intResponse.data);
      if (analyticsResponse?.data?.summary) {
        setAnalyticsSummary(analyticsResponse.data.summary);
      }
    } catch {
      setIntelligence(null);
    } finally {
      setIntelligenceLoading(false);
    }
  };

  useEffect(() => {
    if (authLoading) return;
    if (user) {
      fetchSubscriptionInfo();
      fetchPlanNames();
      fetchAccountConfigStatus();
      fetchIntelligenceSummary();
    }
  }, [authLoading, user]);

  return {
    user,
    isAccountConfigured,
    error,
    subscriptionInfo,
    planNamesById,
    isUserNew,
    intelligence,
    analyticsSummary,
    intelligenceLoading,
  };
}
